import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { Parser as HtmlToReactParser } from 'html-to-react';
import ResponsiveEmbed from 'react-responsive-embed';
import ReactMarkdown from 'react-markdown/with-html';
import RemarkShortcodes from 'remark-shortcodes';
import InstagramEmbed from 'react-instagram-embed';

import { isRelativeLink, getLinkProps } from './links';
import { renderPossiblyOptimizedImage } from './images';
import Quote from '../components/Quote';
import WorkdetailComponents from '../components/Workdetail';
import WorkdetailSection from '../components/Workdetail/Section';

const htmlToReactParser = new HtmlToReactParser();

export const linkRenderer = (props, optimizedImages) => {
  const LinkElement = isRelativeLink(props.href) ? Link : 'a';
  const linkProps = getLinkProps(props.href);

  return (
    <LinkElement {...linkProps}>
      {['string', 'undefined'].includes(typeof props.children)
        ? props.children
        : parseHtmlElement(props.children, optimizedImages)}
    </LinkElement>
  );
};

linkRenderer.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

const DEFAULT_EMBED_RATIO = '16:3';

const EmbedWrapper = styled.div.attrs({ className: 'embed' })`
  max-width: 970px;
`;

export const workdetailEmbedRenderer = obj => (
  <EmbedWrapper>
    <ResponsiveEmbed src={obj.element.props.src} allowFullScreen ratio="16:9" />
  </EmbedWrapper>
);

const isFroalaVideo = className =>
  ['fr-video', 'fr-draggable'].some(cn => className.includes(cn));

export const embedRenderer = element => {
  const includeVideoRatio =
    (element.props.className && isFroalaVideo(element.props.className)) ||
    (element.props.children &&
      element.props.children.props.className &&
      isFroalaVideo(element.props.children.props.className));

  return (
    <EmbedWrapper>
      <ResponsiveEmbed
        src={element.props.src}
        allowFullScreen
        ratio={includeVideoRatio ? '16:9' : DEFAULT_EMBED_RATIO}
      />
    </EmbedWrapper>
  );
};

embedRenderer.propTypes = {
  value: PropTypes.string.isRequired
};

const parseHtmlElement = (elem, optimizedImages) => {
  const element =
    typeof elem === 'object' && elem.element ? elem.element : elem;

  if (typeof element === 'undefined') {
    return element;
  }

  // remove "Powered by Froala"
  if (element.type === 'p' && element.props['data-f-id'] === 'pbf') {
    return null;
  }

  if (element.type === 'a') {
    return linkRenderer(element.props, optimizedImages);
  }

  if (
    element.type === 'p' &&
    element.props &&
    element.props.children &&
    element.props.children.type === 'a'
  ) {
    return linkRenderer(element.props.children.props, optimizedImages);
  }

  if (element.props && element.type === 'iframe') {
    return embedRenderer(element);
  }

  if (
    element.type &&
    element.props &&
    element.props.children &&
    element.props.children.type === 'iframe'
  ) {
    return embedRenderer(element.props.children);
  }

  if (
    element.type === 'p' &&
    typeof element.props.children === 'string' &&
    element.props.children.includes('iframe')
  ) {
    return embedRenderer(element.props.children);
  }

  if (element.props && element.type === 'img') {
    return imageRenderer(element.props, optimizedImages);
  }

  if (
    element.type &&
    element.props &&
    element.props.children &&
    element.props.children.type === 'img'
  ) {
    return imageRenderer(element.props.children.props, optimizedImages);
  }

  if (element.type === 'blockquote') {
    return htmlBlockquoteRenderer(element.props);
  }

  if (element.type === 'article' && element.props && element.props.children) {
    return (
      <div>
        {React.Children.map(element.props.children, args =>
          parseHtmlElement(args, optimizedImages)
        )}
      </div>
    );
  }

  if (element.type && element.props && element.props.children) {
    return (
      <element.type>
        {React.Children.map(element.props.children, args =>
          parseHtmlElement(args, optimizedImages)
        )}
      </element.type>
    );
  }

  return element;
};

export const parsedHtmlRenderer = ({ value }, optimizedImages) => {
  const parsedReact = htmlToReactParser.parse(value);

  if (Array.isArray(parsedReact)) {
    return parsedReact.map(e => parseHtmlElement(e, optimizedImages));
  } else if (
    typeof parsedReact === 'object' &&
    parsedReact.type &&
    parsedReact.props
  ) {
    return parseHtmlElement(parsedReact, optimizedImages);
  }

  return value;
};

parsedHtmlRenderer.propTypes = {
  value: PropTypes.string.isRequired
};

export const imageRenderer = ({ src, ...props }, optimizedImages) => {
  return renderPossiblyOptimizedImage({ src, ...props }, optimizedImages);
};

imageRenderer.propTypes = {
  src: PropTypes.string.isRequired
};

export const htmlBlockquoteRenderer = elem => {
  let instagramUrl = null;
  if (elem.children && elem.children.props && elem.children.props.children) {
    React.Children.forEach(elem.children.props.children, child => {
      if (
        child.props &&
        child.props.href &&
        child.props.href.includes('instagram')
      ) {
        instagramUrl = child.props.href;
      }
    });
  }

  if (instagramUrl) {
    return (
      <div className="embed">
        <InstagramEmbed url={instagramUrl} hideCaption injectScript />
      </div>
    );
  }

  return <Quote>{elem.children}</Quote>;
};

htmlBlockquoteRenderer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export const blockquoteRenderer = ({ children }) => {
  let quote;
  if (Array.isArray(children)) {
    quote = children[0].props.children[0].props.value;
  } else {
    quote = children.props.children;
  }

  const [, quoteText, quoteAttribution] = /^(.*?)--.*?(.+\\?)$/g.exec(quote);
  return (
    <Quote
      text={quoteText}
      attributes={quoteAttribution.split('\\').map(w => w.trim())}
    />
  );
};

blockquoteRenderer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export const shortcodeRenderer = (
  { identifier, attributes },
  optimizedImages
) => {
  const ComponentType = WorkdetailComponents[identifier];
  return ComponentType ? (
    <ComponentType {...attributes} optimizedImages={optimizedImages} />
  ) : null;
};

shortcodeRenderer.propTypes = {
  identifier: PropTypes.string.isRequired,
  attributes: PropTypes.object.isRequired
};

export const workdetailSectionRenderer = ({ value }, optimizedImages) => {
  return (
    <WorkdetailSection>
      <ReactMarkdown
        source={value}
        renderers={{
          link: linkRenderer,
          parsedHtml: workdetailEmbedRenderer,
          image: args => imageRenderer(args, optimizedImages),
          code: args => workdetailSectionRenderer(args, optimizedImages),
          blockquote: blockquoteRenderer,
          shortcode: args => shortcodeRenderer(args, optimizedImages)
        }}
        escapeHtml={false}
        plugins={[RemarkShortcodes]}
      />
    </WorkdetailSection>
  );
};

workdetailSectionRenderer.propTypes = {
  value: PropTypes.string.isRequired
};
